import React, { useEffect, useMemo, useState } from "react";
import { NavLink } from "react-router-dom";
import Cookies from "js-cookie";

function normalizePermissions(raw) {
  if (!raw) return {};
  if (raw instanceof Map) return Object.fromEntries(raw);
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        return Object.fromEntries(
          Object.entries(parsed).map(([k, v]) => [k, !!v]),
        );
      }
    } catch {}
    return {};
  }
  if (typeof raw === "object" && !Array.isArray(raw)) {
    return Object.fromEntries(Object.entries(raw).map(([k, v]) => [k, !!v]));
  }
  return {};
}

const NavSection = ({ label, children }) => {
  const visible = React.Children.toArray(children).filter(Boolean);
  if (!visible.length) return null;
  return (
    <div style={{ marginBottom: "1.5rem" }}>
      <div
        style={{
          fontSize: "0.62rem",
          fontWeight: 700,
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          color: "#94a3b8",
          padding: "0 0.5rem",
          marginBottom: "0.25rem",
        }}
      >
        {label}
      </div>
      <ul
        className="nav flex-column"
        style={{ gap: "1px" }}
      >
        {children}
      </ul>
    </div>
  );
};

const NavItem = ({ to, icon, label }) => (
  <li className="nav-item">
    <NavLink
      to={to}
      className={({ isActive }) =>
        `nav-link d-flex align-items-center gap-2 ${isActive ? "active" : "link-dark"}`
      }
      style={({ isActive }) => ({
        borderRadius: "6px 24px 24px 6px",
        padding: "0.5rem 1rem",
        fontWeight: isActive ? 600 : 400,
      })}
    >
      <i
        className={`${icon} fa-fw`}
        style={{ fontSize: "0.85rem", opacity: 0.65 }}
      />
      {label}
    </NavLink>
  </li>
);

const Sidenav = ({ domain }) => {
  const [userType, setUserType] = useState("");
  const [permissions, setPermissions] = useState({});

  useEffect(() => {
    const id = Cookies.get("userId");
    const cookieType = (Cookies.get("userType") || "").trim().toLowerCase();
    setUserType(cookieType);

    if (!id) {
      setPermissions(normalizePermissions(Cookies.get("userPermissions")));
      return;
    }

    const ac = new AbortController();
    (async () => {
      try {
        const res = await fetch(`/api/users/${id}`, {
          method: "GET",
          redirect: "follow",
          signal: ac.signal,
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();

        setUserType(String(data?.type || cookieType).toLowerCase());
        setPermissions(normalizePermissions(data?.permissions));
        Cookies.set(
          "userPermissions",
          JSON.stringify(data?.permissions || {}),
          { expires: 1, path: "/" },
        );
      } catch {
        setPermissions(normalizePermissions(Cookies.get("userPermissions")));
      }
    })();

    return () => ac.abort();
  }, []);

  const isSuperadmin = useMemo(() => userType === "superadmin", [userType]);
  const showDashboard = useMemo(
    () => !!userType && userType !== "user",
    [userType],
  );
  const canView = (permKey) => isSuperadmin || !!permissions[permKey];

  return (
    <div
      style={{
        width: "220px",
        minWidth: "220px",
        height: "calc(100vh - 94px)",
        backgroundColor: "white",
        borderRight: "1px solid #e2e8f0",
        display: "flex",
        flexDirection: "column",
        padding: "1.25rem 0.75rem 1rem",
        overflowY: "auto",
      }}
    >
      <div style={{ flex: 1 }}>
        <NavSection label="BLEEP Reports">
          {showDashboard && (
            <NavItem
              to="/dashboard"
              icon="fas fa-bolt"
              label="Dashboard"
            />
          )}
          {canView("bleepMap") && (
            <NavItem
              to="/bleepmap"
              icon="fas fa-earth"
              label="BLEEP Map"
            />
          )}
        </NavSection>

        <NavSection label="BleST">
          {canView("blestOrders") && (
            <NavItem
              to="/blestorders"
              icon="fas fa-shopping-cart"
              label="BleST Orders"
            />
          )}
          {canView("productSkus") && (
            <NavItem
              to="/productskus"
              icon="fas fa-box"
              label="Product SKUs"
            />
          )}
          {canView("myBlest") && (
            <NavItem
              to="/myblest"
              icon="fas fa-note-sticky"
              label="My BleST"
            />
          )}
          {canView("blestMap") && (
            <NavItem
              to="/blestmap"
              icon="fas fa-earth"
              label="BleST Map"
            />
          )}
        </NavSection>

        <NavSection label="Promotion">
          {canView("ads") && (
            <NavItem
              to="/buygenuine"
              icon="fas fa-shield"
              label="Buy Genuine"
            />
          )}
          {/* Banner Ads hidden — re-enable when ready */}
          {false && canView("ads") && (
            <NavItem
              to="/ads"
              icon="fa-solid fa-ad"
              label="Banner Ads"
            />
          )}
        </NavSection>

        <NavSection label="Internal">
          {canView("articles") && (
            <NavItem
              to="/articles"
              icon="fas fa-newspaper"
              label="Articles"
            />
          )}
          {canView("clients") && (
            <NavItem
              to="/clients"
              icon="fa-solid fa-handshake"
              label="Clients"
            />
          )}
          {canView("accounts") && (
            <NavItem
              to="/accounts"
              icon="fas fa-key"
              label="Accounts"
            />
          )}
          {isSuperadmin && (
            <NavItem
              to="/analytics"
              icon="fas fa-line-chart"
              label="Analytics"
            />
          )}
        </NavSection>
      </div>

      <div
        style={{
          fontSize: "0.7rem",
          color: "#94a3b8",
          paddingTop: "0.75rem",
          borderTop: "1px solid #e2e8f0",
        }}
      >
        © {new Date().getFullYear()} BLEEP, Anti-Counterfeit Network Africa
      </div>
    </div>
  );
};

export default Sidenav;
